import React, { useState, useEffect } from "react";
import { auditoria } from "../../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Shield, Search, ChevronLeft, ChevronRight, User, Clock } from "lucide-react";
import { toast } from "sonner";
import { cn } from "../../lib/utils";

const MODULOS = [
  { value: "all", label: "Todos os Módulos" },
  { value: "notas_fiscais", label: "Notas Fiscais" },
  { value: "importacoes", label: "Importações" },
  { value: "atividades", label: "Atividades" },
  { value: "usuarios", label: "Usuários" },
];

const ACOES = [
  { value: "all", label: "Todas as Ações" },
  { value: "create", label: "Criação" },
  { value: "update", label: "Atualização" },
  { value: "delete", label: "Exclusão" },
];

const acaoBadge = (acao) => {
  const map = {
    create: { label: "Criação", className: "bg-green-100 text-green-700" },
    update: { label: "Atualização", className: "bg-blue-100 text-blue-700" },
    delete: { label: "Exclusão", className: "bg-red-100 text-red-700" },
  };
  const style = map[acao] || { label: acao, className: "bg-gray-100 text-gray-700" };
  return (
    <span className={cn("px-2 py-0.5 rounded text-xs font-medium", style.className)}>
      {style.label}
    </span>
  );
};

const moduloBadge = (modulo) => {
  const map = {
    notas_fiscais: "Notas Fiscais",
    importacoes: "Importações",
    atividades: "Atividades",
    usuarios: "Usuários",
  };
  return map[modulo] || modulo;
};

export function AuditoriaPage() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [moduloFilter, setModuloFilter] = useState("all");
  const [acaoFilter, setAcaoFilter] = useState("all");
  const [page, setPage] = useState(0);
  const LIMIT = 20;

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = { limit: LIMIT, skip: page * LIMIT };
      if (moduloFilter !== "all") params.modulo = moduloFilter;
      if (acaoFilter !== "all") params.acao = acaoFilter;
      const res = await auditoria.list(params);
      setLogs(res.data.registros || []);
      setTotal(res.data.total || 0);
    } catch (error) {
      toast.error("Erro ao carregar logs de auditoria");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, [moduloFilter, acaoFilter, page]);

  const formatDateTime = (iso) => {
    if (!iso) return "-";
    const d = new Date(iso);
    return d.toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" });
  };

  const totalPages = Math.ceil(total / LIMIT);

  return (
    <div className="space-y-6" data-testid="auditoria-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-heading font-bold">Log de Auditoria</h1>
          <p className="text-muted-foreground">Histórico de ações críticas do sistema</p>
        </div>
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Shield className="h-4 w-4" />
          <span>{total} registros</span>
        </div>
      </div>

      <div className="flex items-center gap-4">
        <Select value={moduloFilter} onValueChange={(v) => { setModuloFilter(v); setPage(0); }}>
          <SelectTrigger className="w-48" data-testid="filter-modulo">
            <SelectValue placeholder="Módulo" />
          </SelectTrigger>
          <SelectContent>
            {MODULOS.map((m) => (
              <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={acaoFilter} onValueChange={(v) => { setAcaoFilter(v); setPage(0); }}>
          <SelectTrigger className="w-48" data-testid="filter-acao">
            <SelectValue placeholder="Ação" />
          </SelectTrigger>
          <SelectContent>
            {ACOES.map((a) => (
              <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12 text-muted-foreground">Carregando...</div>
          ) : logs.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
              <Shield className="h-12 w-12 mb-3" />
              <p>Nenhum registro de auditoria encontrado</p>
            </div>
          ) : (
            <div className="divide-y">
              {logs.map((log) => (
                <div key={log.id} className="px-6 py-4 hover:bg-slate-50 transition-colors" data-testid={`audit-entry-${log.id}`}>
                  <div className="flex items-start justify-between">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        {acaoBadge(log.acao)}
                        <span className="text-sm font-medium text-slate-600">{moduloBadge(log.modulo)}</span>
                      </div>
                      <p className="text-sm">{log.descricao}</p>
                    </div>
                    <div className="text-right space-y-1 flex-shrink-0 ml-4">
                      <div className="flex items-center gap-1 text-xs text-muted-foreground">
                        <User className="h-3 w-3" />
                        <span>{log.usuario_nome}</span>
                      </div>
                      <div className="flex items-center gap-1 text-xs text-muted-foreground">
                        <Clock className="h-3 w-3" />
                        <span>{formatDateTime(log.data_hora)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Página {page + 1} de {totalPages}
          </p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(p => p - 1)} data-testid="audit-prev-page">
              <ChevronLeft className="h-4 w-4 mr-1" /> Anterior
            </Button>
            <Button variant="outline" size="sm" disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)} data-testid="audit-next-page">
              Próximo <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
